/**
 * <collection-slider> — grid on desktop, Flickity slider below 990px.
 *
 *   <collection-slider data-breakpoint="989" data-flickity-config='{"…":…}'>
 *     <ul data-component="track"> <li>…</li> </ul>
 *   </collection-slider>
 *
 * The same markup serves both: on small screens the track is given the
 * `.js-carousel` class and the options the rest of the theme uses, then
 * Flickity is initialised on it. Above the breakpoint the instance is
 * destroyed and those hooks are removed, so the CSS grid takes over again.
 *
 * The options live in `data-flickity-config`, never in `data-flickity`:
 * Flickity's own htmlInit picks up every `[data-flickity]` element on document
 * ready and initialises it, which would make this a slider on desktop too.
 *
 * matchMedia is used rather than a resize listener: the callback fires only
 * when the breakpoint is actually crossed, not on every resize frame.
 */
(() => {
  if (customElements.get('collection-slider')) return;

  const DEFAULTS = {
    cellAlign: 'left',
    contain: true,
    pageDots: true,
    prevNextButtons: false,
    draggable: true,
    dragThreshold: 15,
    wrapAround: false,
    groupCells: false,
  };

  class CollectionSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-component="track"]');
      if (!this.track) return;

      const breakpoint = parseInt(this.dataset.breakpoint, 10) || 989;
      this.media = window.matchMedia(`(max-width: ${breakpoint}px)`);

      this.sync();
      this.media.addEventListener('change', this.sync);
    }

    disconnectedCallback() {
      this.media?.removeEventListener('change', this.sync);
      this.destroy();
    }

    /** Arrow function: `this` stays the element and the same reference can be
        handed back to removeEventListener. */
    sync = () => {
      if (this.media.matches) this.build();
      else this.destroy();
    };

    get options() {
      let options = {};
      if (this.dataset.flickityConfig) {
        try {
          options = JSON.parse(this.dataset.flickityConfig);
        } catch (error) {
          console.warn('[collection-slider] Invalid data-flickity-config JSON', error);
        }
      }
      return Object.assign({}, DEFAULTS, options);
    }

    build() {
      if (this.flkty) return;

      // theme.js carries Flickity and runs before this file, but a slow or
      // re-ordered bundle would leave it undefined — wait for load and retry
      // once rather than silently rendering a static list.
      if (typeof Flickity === 'undefined') {
        if (this.pendingBuild) return;
        this.pendingBuild = true;
        window.addEventListener(
          'load',
          () => {
            this.pendingBuild = false;
            if (this.media.matches) this.build();
          },
          { once: true }
        );
        return;
      }

      // Already initialised by something else (a re-render in the theme
      // editor, say) — don't stack a second instance on the same element.
      if (Flickity.data(this.track)) return;

      // The hooks the theme's own carousels use, added only while the slider
      // is live so nothing else picks the track up as a carousel on desktop.
      this.track.classList.add('js-carousel');
      this.track.setAttribute('data-flickity-config', JSON.stringify(this.options));
      this.classList.add('is-slider');

      this.flkty = new Flickity(this.track, this.options);
      // Cells are measured before the images decide their height, so lay out
      // again once the browser has settled.
      requestAnimationFrame(() => this.flkty?.resize());
    }

    destroy() {
      if (!this.flkty) return;

      this.flkty.destroy();
      this.flkty = null;
      this.track.classList.remove('js-carousel');
      this.track.removeAttribute('data-flickity-config');
      this.classList.remove('is-slider');
    }
  }

  customElements.define('collection-slider', CollectionSlider);
})();
